import React from 'react'
import { useAuth } from '../auth/AuthContext'
import { Roles, hasAnyRole } from '../auth/rbac'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { asArray } from '../lib/collections'
import { useLocation } from 'react-router-dom'
import MenuGuideButton from '../components/MenuGuideButton'
import { formatMenuInstant } from '../lib/date'

function formatSurveyResponsesForDisplay(val) {
  if (val == null || val === '') return null
  if (typeof val === 'object') return JSON.stringify(val, null, 2)
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val)
      return typeof parsed === 'object' && parsed !== null ? JSON.stringify(parsed, null, 2) : val
    } catch {
      return val
    }
  }
  return String(val)
}

const CATEGORY_LABELS = {
  HIDDEN_INSURANCE_MONEY: '숨은보험금찾기',
  COVERAGE_ANALYSIS: '보장분석',
  PRODUCT_AUTO: '자동차보험',
  PRODUCT_COMPREHENSIVE: '종합보험',
  PRODUCT_PENSION: '연금보험',
  PRODUCT_WHOLE_LIFE: '종신보험',
  PRODUCT_CARE: '간병보험',
  PRODUCT_FIRE: '화재보험',
  INSURANCE_REMODELING: '보험리모델링',
  PERSONAL_INTRO: '본인소개홈페이지',
}

const ORG_LEAD_RESPONSE_LABELS = {
  careerType: '지원경력',
  careerCompanyType: '경력회사',
  birthDate: '생년월일',
  preferredRegion: '희망근무지역',
  preferredContactTime: '희망 연락 시간',
  experienceArea: '경력 분야',
}

function assigneeDisplayName(u) {
  if (!u || u.id == null) return ''
  return (u.name && String(u.name).trim()) || u.email || `사용자 ${u.id}`
}

function assigneeOrgSuffix(u) {
  const n = u?.orgName != null ? String(u.orgName).trim() : ''
  return n ? ` · ${n}` : ''
}

function manualLeadCreatorLabel(it) {
  if (!it || it.source !== 'MANUAL_UPLOAD') return ''
  const name = String(it.creatorUserName || it.creatorUserEmail || '').trim()
  const org = String(it.creatorOrgName || '').trim()
  return [name, org].filter(Boolean).join(' · ')
}

function buildOrgLeadResponseRows(formResponses) {
  if (!formResponses || typeof formResponses !== 'object') return []
  return Object.entries(formResponses)
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([key, value]) => ({
      key,
      label: ORG_LEAD_RESPONSE_LABELS[key] || key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    }))
}

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

/** 조직홈페이지 리드는 일반 고객이 아니라 담당 배정 UI를 숨김(필요 시 true). */
const SHOW_ORG_LANDING_LEAD_ASSIGN_UI = false

const STATS_PRESETS = [
  { value: 'TODAY', label: '당일' },
  { value: 'YESTERDAY', label: '어제' },
  { value: 'LAST_7_DAYS', label: '최근 7일' },
  { value: 'LAST_30_DAYS', label: '최근 30일' },
  { value: 'THIS_MONTH', label: '이번 달' },
]

function formatPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—'
  return `${Number(v)}%`
}

function ChannelStatsCard({ title, m }) {
  if (!m) return null
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-bold text-slate-800">{title}</div>
      <div className="mt-3 space-y-1.5 text-xs">
        <div className="flex justify-between gap-2">
          <span className="text-slate-500">페이지 조회</span>
          <span className="font-semibold text-slate-900">{m.views ?? 0}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-slate-500">상담요청</span>
          <span className="font-semibold text-slate-900">{m.consultRequests ?? 0}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-slate-500">상담완료</span>
          <span className="font-semibold text-slate-900">{m.consultCompleted ?? 0}</span>
        </div>
        <div className="flex justify-between gap-2 border-t border-slate-100 pt-1.5">
          <span className="text-slate-500">조회 대비 요청율</span>
          <span className="font-semibold text-slate-900">{formatPct(m.viewToRequestRate)}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-slate-500">요청 대비 완료율</span>
          <span className="font-semibold text-slate-900">{formatPct(m.requestToCompletedRate)}</span>
        </div>
      </div>
    </div>
  )
}

function MonthlyMetricLine({ m }) {
  if (!m) return <span className="text-slate-400">—</span>
  return (
    <span className="text-[11px] leading-snug text-slate-700">
      조회 {m.views ?? 0} · 요청 {m.consultRequests ?? 0} · 완료 {m.consultCompleted ?? 0}
      <span className="text-slate-500">
        {' '}
        (요청율 {formatPct(m.viewToRequestRate)}, 완료율 {formatPct(m.requestToCompletedRate)})
      </span>
    </span>
  )
}

export default function MyConsultationsPage() {

  const queryClient = useQueryClient()
  const location = useLocation()
  const { user } = useAuth()
  const canUseOrgHomepageLeads = React.useMemo(() => {
    if (hasAnyRole(user, [Roles.OPERATOR])) return true
    const raw = user?.visibleMenuKeys ?? user?.visible_menu_keys
    return Array.isArray(raw) && raw.includes('MENU_SALES_ORG_HOMEPAGE')
  }, [user])
  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState(null)
  const [statusSaving, setStatusSaving] = React.useState(false)
  const [tab, setTab] = React.useState('ALL') // ALL, ORG_HOMEPAGE, PERSONAL_LANDING, MANUAL
  const [channelFilter, setChannelFilter] = React.useState('ALL') // ALL, ORG_LANDING, CATEGORY_LANDING (개인/홍보용페이지 탭에서만 사용)
  const [manualName, setManualName] = React.useState('')
  const [manualPhone, setManualPhone] = React.useState('')
  const [manualMessage, setManualMessage] = React.useState('')
  const [manualBulk, setManualBulk] = React.useState('')
  const [manualSubmitting, setManualSubmitting] = React.useState(false)
  const [crmRegisterBusy, setCrmRegisterBusy] = React.useState(false)

  const crmLookupDigits = React.useMemo(() => {
    const d = String(selected?.customerPhone || '').replace(/\D/g, '')
    if (d.length !== 10 && d.length !== 11) return ''
    return d
  }, [selected?.customerPhone])

  const crmRegisteredLookup = useQuery({
    queryKey: ['customer-management', 'lookup-by-phone', crmLookupDigits],
    queryFn: async () => {
      const res = await api.get('/customer-management/customers/lookup-by-phone', {
        params: { customerPhone: crmLookupDigits },
        __silent: true,
      })
      return res.data?.data ?? res.data
    },
    enabled: Boolean(open && crmLookupDigits && ['PERSONAL_LANDING', 'CATEGORY_LANDING', 'MANUAL_UPLOAD'].includes(selected?.source)),
    staleTime: 15_000,
  })

  const crmAlreadyRegistered = Boolean(crmRegisteredLookup.data?.registered)
  const [manualOneModalOpen, setManualOneModalOpen] = React.useState(false)
  const [manualBulkModalOpen, setManualBulkModalOpen] = React.useState(false)
  const [assignOptions, setAssignOptions] = React.useState([])
  const [assignPick, setAssignPick] = React.useState('')
  const [assignSearch, setAssignSearch] = React.useState('')
  const [assignLoading, setAssignLoading] = React.useState(false)
  const [assignSaving, setAssignSaving] = React.useState(false)
  const [statsPreset, setStatsPreset] = React.useState('LAST_30_DAYS')
  const [statsOrgId, setStatsOrgId] = React.useState('')
  const [statsModalOpen, setStatsModalOpen] = React.useState(false)
  const [monthlyStatsOpen, setMonthlyStatsOpen] = React.useState(false)
  const customerPrefillAppliedRef = React.useRef('')

  const focusCustomerRefId = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(location?.search || '')
      const v = sp.get('customerRefId')
      return v ? String(v).trim() : ''
    } catch {
      return ''
    }
  }, [location?.search])

  React.useEffect(() => {
    if (statsModalOpen) setMonthlyStatsOpen(false)
  }, [statsModalOpen])

  React.useEffect(() => {
    if (!canUseOrgHomepageLeads && tab === 'ORG_HOMEPAGE') {
      setTab('ALL')
      setChannelFilter('ALL')
    }
  }, [canUseOrgHomepageLeads, tab])

  React.useEffect(() => {
    if (!canUseOrgHomepageLeads && statsOrgId) {
      setStatsOrgId('')
    }
  }, [canUseOrgHomepageLeads, statsOrgId])

  React.useEffect(() => {
    if (!focusCustomerRefId || customerPrefillAppliedRef.current === focusCustomerRefId) return
    customerPrefillAppliedRef.current = focusCustomerRefId
    let ignore = false
    api.get(`/customer-management/customers/${encodeURIComponent(focusCustomerRefId)}`, { __silent: true })
      .then((res) => {
        if (ignore) return
        const data = unwrap(res)
        const customer = data?.customer || data
        setManualName(String(customer?.customerName || '').trim())
        setManualPhone(String(customer?.customerPhone || '').replace(/\D/g, ''))
        setManualMessage('고객상세 상담·영업 플로우에서 연결')
        setTab('MANUAL')
        setManualOneModalOpen(true)
      })
      .catch(() => {
        if (ignore) return
        setManualMessage('고객상세 상담·영업 플로우에서 연결')
        setTab('MANUAL')
        setManualOneModalOpen(true)
      })
    return () => { ignore = true }
  }, [focusCustomerRefId])

  // 개인홈페이지 리드
  const personalLeadsQuery = useQuery({
    queryKey: ['me', 'leads', 'personal'],
    queryFn: async () => {
      const res = await api.get('/leads/me', { params: { page: 0, size: 200 }, __silent: true })
      const page = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const arr = Array.isArray(page?.content) ? page.content : (Array.isArray(page?.items) ? page.items : (Array.isArray(page) ? page : []))
      return Array.isArray(arr) ? arr.map(it => ({ ...it, source: 'PERSONAL_LANDING', sourceLabel: '개인홈페이지', channel: '개인홈페이지' })) : []
    }
  })

  // 홍보용페이지 리드
  const categoryLeadsQuery = useQuery({
    queryKey: ['me', 'category-landing-leads'],
    queryFn: async () => {
      const res = await api.get('/category-landing-leads/me', { params: { page: 0, size: 200 }, __silent: true })
      const page = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const arr = Array.isArray(page?.content) ? page.content : (Array.isArray(page?.items) ? page.items : (Array.isArray(page) ? page : []))
      return Array.isArray(arr) ? arr.map(it => ({
        ...it,
        source: 'CATEGORY_LANDING',
        sourceLabel: CATEGORY_LABELS[it.category] || it.category || '홍보용페이지',
        categoryLabel: CATEGORY_LABELS[it.category] || it.category || '',
        channel: '홍보용페이지',
      })) : []
    }
  })

  const salesStatsQuery = useQuery({
    queryKey: ['sales-stats', 'me', statsPreset, canUseOrgHomepageLeads, canUseOrgHomepageLeads ? statsOrgId : ''],
    queryFn: async () => {
      const params = { preset: statsPreset }
      if (canUseOrgHomepageLeads && statsOrgId) params.orgId = statsOrgId
      const res = await api.get('/sales-stats/me', { params, __silent: true })
      const d = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      return d && typeof d === 'object' ? d : null
    },
    refetchOnWindowFocus: false,
  })

  const manualLeadsQuery = useQuery({
    queryKey: ['me', 'sales-manual-leads'],
    queryFn: async () => {
      const res = await api.get('/sales-manual-leads/me', { params: { page: 0, size: 200 }, __silent: true })
      const page = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const arr = Array.isArray(page?.content) ? page.content : (Array.isArray(page?.items) ? page.items : (Array.isArray(page) ? page : []))
      return Array.isArray(arr) ? arr : []
    },
    enabled: tab === 'MANUAL' || tab === 'ALL',
    refetchOnWindowFocus: false,
  })

  // 조직홈페이지 리드 (조직 관리자급, 리쿠르팅용)
  const orgLeadsQuery = useQuery({
    queryKey: ['me', 'leads', 'org'],
    queryFn: async () => {
      const res = await api.get('/org-landing-leads/me', { params: { page: 0, size: 200 }, __silent: true })
      const page = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const arr = Array.isArray(page?.content) ? page.content : (Array.isArray(page?.items) ? page.items : (Array.isArray(page) ? page : []))
      return Array.isArray(arr) ? arr.map(it => ({
        ...it,
        source: 'ORG_HOMEPAGE',
        sourceLabel: '조직홈페이지',
        categoryLabel: it.categoryLabel || '리크루팅',
        channel: it.channel || '조직홈페이지',
      })) : []
    },
    refetchOnWindowFocus: false,
    enabled: canUseOrgHomepageLeads,
  })

  // 리드를 합치고 탭/필터에 따라 정렬 (최신순)
  const items = React.useMemo(() => {
    const orgLeads = canUseOrgHomepageLeads && Array.isArray(orgLeadsQuery.data) ? orgLeadsQuery.data : []
    const personalLeads = Array.isArray(personalLeadsQuery.data) ? personalLeadsQuery.data : []
    const categoryLeads = Array.isArray(categoryLeadsQuery.data) ? categoryLeadsQuery.data : []
    const manualLeads = Array.isArray(manualLeadsQuery.data) ? manualLeadsQuery.data : []

    let all = []

    // 탭에 따라 필터링
    if (tab === 'MANUAL') {
      all = manualLeads
    } else if (tab === 'ORG_HOMEPAGE') {
      // 조직홈페이지만 (리쿠르팅)
      all = orgLeads
    } else if (tab === 'PERSONAL_LANDING') {
      // 소개/랜딩페이지 (일반 고객)
      all = [...personalLeads, ...categoryLeads]
      
      // 소개/랜딩페이지 탭 내에서 채널 필터링
      if (channelFilter === 'ORG_LANDING') {
        all = all.filter(it => it.source === 'PERSONAL_LANDING')
      } else if (channelFilter === 'CATEGORY_LANDING') {
        all = all.filter(it => it.source === 'CATEGORY_LANDING')
      }
    } else {
      // 전체
      all = [...orgLeads, ...personalLeads, ...categoryLeads, ...manualLeads]
    }
    
    // 최신순 정렬
    return all.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return bTime - aTime
    })
  }, [canUseOrgHomepageLeads, orgLeadsQuery.data, personalLeadsQuery.data, categoryLeadsQuery.data, manualLeadsQuery.data, tab, channelFilter])

  const orgLeads = canUseOrgHomepageLeads && Array.isArray(orgLeadsQuery.data) ? orgLeadsQuery.data : []
  const personalLeads = Array.isArray(personalLeadsQuery.data) ? personalLeadsQuery.data : []
  const categoryLeads = Array.isArray(categoryLeadsQuery.data) ? categoryLeadsQuery.data : []
  const manualLeads = Array.isArray(manualLeadsQuery.data) ? manualLeadsQuery.data : []
  const allLeadsCount = orgLeads.length + personalLeads.length + categoryLeads.length + manualLeads.length
  const orgPendingCount = orgLeads.filter((it) => String(it?.status || 'NEW').toUpperCase() === 'NEW').length
  const orgContactedCount = orgLeads.filter((it) => String(it?.status || '').toUpperCase() === 'CONTACTED').length
  const orgDoneCount = orgLeads.filter((it) => ['DONE', 'COMPLETED'].includes(String(it?.status || '').toUpperCase())).length

  const isLoading = (canUseOrgHomepageLeads && orgLeadsQuery.isLoading) || personalLeadsQuery.isLoading || categoryLeadsQuery.isLoading || manualLeadsQuery.isLoading
  const isError = (canUseOrgHomepageLeads && orgLeadsQuery.isError) || personalLeadsQuery.isError || categoryLeadsQuery.isError || manualLeadsQuery.isError
  const isFetching = (canUseOrgHomepageLeads && orgLeadsQuery.isFetching) || personalLeadsQuery.isFetching || categoryLeadsQuery.isFetching || manualLeadsQuery.isFetching

  React.useEffect(() => {
    const src = selected?.source
    const assignableRootOrgId = selected?.assignableRootOrgId || selected?.orgId
    const orgEligible =
      SHOW_ORG_LANDING_LEAD_ASSIGN_UI && src === 'ORG_HOMEPAGE' && assignableRootOrgId && selected?.canAssignLead
    const salesEligible =
      ['PERSONAL_LANDING', 'CATEGORY_LANDING', 'MANUAL_UPLOAD'].includes(src) && assignableRootOrgId && selected?.canAssignLead
    if (!open || !selected || !(orgEligible || salesEligible)) {
      setAssignOptions([])
      setAssignPick('')
      return
    }
    let ignore = false
    setAssignLoading(true)
    api.get('/org-landing-leads/me/assignable-members', {
      params: { orgId: assignableRootOrgId, includeDescendants: true },
      __silent: true,
    })
      .then((res) => {
        const body = res?.data?.data !== undefined ? res.data.data : res.data
        const arr = Array.isArray(body) ? body : []
        if (!ignore) {
          setAssignOptions(arr)
          const cur = selected.assignedUserId != null ? String(selected.assignedUserId) : ''
          setAssignPick(cur)
        }
      })
      .catch(() => {
        if (!ignore) setAssignOptions([])
      })
      .finally(() => {
        if (!ignore) setAssignLoading(false)
      })
    return () => {
      ignore = true
    }
  }, [open, selected?.id, selected?.orgId, selected?.assignableRootOrgId, selected?.canAssignLead, selected?.source, selected?.assignedUserId])

  React.useEffect(() => {
    setAssignSearch('')
  }, [selected?.id])

  const assignFilteredSorted = React.useMemo(() => {
    const q = assignSearch.trim().toLowerCase()
    const sorted = [...assignOptions].sort((a, b) =>
      assigneeDisplayName(a).localeCompare(assigneeDisplayName(b), 'ko'),
    )
    if (!q) return sorted
    return sorted.filter((u) => {
      const name = String(u?.name || '').toLowerCase()
      const email = String(u?.email || '').toLowerCase()
      const org = String(u?.orgName || '').toLowerCase()
      return name.includes(q) || email.includes(q) || org.includes(q)
    })
  }, [assignOptions, assignSearch])

  const submitManualOne = async () => {
    const name = (manualName || '').trim()
    const phone = (manualPhone || '').replace(/\D/g, '')
    if (!name) {
      try { window?.toast?.show?.('이름을 입력해 주세요.', 'error') } catch { }
      return
    }
    if (!(phone.length === 10 || phone.length === 11)) {
      try { window?.toast?.show?.('연락처는 숫자 10~11자리로 입력해 주세요.', 'error') } catch { }
      return
    }
    setManualSubmitting(true)
    try {
      await api.post('/sales-manual-leads/me', {
        customerName: name,
        customerPhone: phone,
        message: (manualMessage || '').trim() || null,
      })
      try { window?.toast?.show?.('등록되었습니다.', 'success') } catch { }
      setManualName('')
      setManualPhone('')
      setManualMessage('')
      setManualOneModalOpen(false)
      await manualLeadsQuery.refetch()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '등록 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setManualSubmitting(false)
    }
  }

  const submitManualBulk = async () => {
    const lines = (manualBulk || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
    const items = lines.map((line) => {
      const parts = line.split(',').map((s) => String(s || '').trim())
      return {
        customerName: parts[0] || '',
        customerPhone: (parts[1] || '').replace(/\D/g, ''),
        message: parts.slice(2).join(',').trim() || null,
      }
    }).filter((r) => r.customerName && (r.customerPhone.length === 10 || r.customerPhone.length === 11))
    if (items.length === 0) {
      try { window?.toast?.show?.('유효한 행이 없습니다. 형식: 이름,연락처[,메모]', 'error') } catch { }
      return
    }
    setManualSubmitting(true)
    try {
      await api.post('/sales-manual-leads/me/batch', { items })
      try { window?.toast?.show?.(`${items.length}건 등록되었습니다.`, 'success') } catch { }
      setManualBulk('')
      setManualBulkModalOpen(false)
      await manualLeadsQuery.refetch()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '일괄 등록 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setManualSubmitting(false)
    }
  }

  const submitLeadAssign = async () => {
    if (!selected?.id) return
    const src = selected?.source
    if (src === 'ORG_HOMEPAGE' && !canUseOrgHomepageLeads) return
    let path = ''
    if (src === 'ORG_HOMEPAGE') path = `/org-landing-leads/me/${encodeURIComponent(selected.id)}/assign`
    else if (src === 'PERSONAL_LANDING') path = `/leads/me/${encodeURIComponent(selected.id)}/assign`
    else if (src === 'MANUAL_UPLOAD') path = `/sales-manual-leads/me/${encodeURIComponent(selected.id)}/assign`
    else if (src === 'CATEGORY_LANDING') path = `/category-landing-leads/me/${encodeURIComponent(selected.id)}/assign`
    if (!path) return
    setAssignSaving(true)
    try {
      const assigneeUserId = assignPick === '' ? null : Number(assignPick)
      const res = await api.patch(path, { assigneeUserId })
      const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      try { window?.toast?.show?.('담당 배정이 반영되었습니다.', 'success') } catch { }
      if (data && typeof data === 'object' && data.id != null) {
        setSelected((prev) => (prev && String(prev.id) === String(data.id) ? { ...prev, ...data } : prev))
      }
      await Promise.all([
        canUseOrgHomepageLeads ? orgLeadsQuery.refetch() : Promise.resolve(),
        personalLeadsQuery.refetch(),
        categoryLeadsQuery.refetch(),
        manualLeadsQuery.refetch(),
      ])
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '배정 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setAssignSaving(false)
    }
  }

  const focusLeadId = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(location?.search || '')
      const v = sp.get('leadId')
      return v ? String(v).trim() : ''
    } catch {
      return ''
    }
  }, [location?.search])

  // 알림 클릭으로 들어온 경우(leadId), 목록 로드 후 자동으로 상세 모달을 연다.
  React.useEffect(() => {
    if (!focusLeadId) return
    if (isLoading || isFetching) return
    if (!Array.isArray(items) || items.length === 0) return
    const found = items.find((it) => it?.id != null && String(it.id) === String(focusLeadId))
    if (!found) return
    setSelected(found)
    setOpen(true)
    // 한 번 열고 나면 쿼리 파라미터를 제거(새로고침 시 반복 오픈 방지)
    try {
      const u = new URL(window.location.href)
      u.searchParams.delete('leadId')
      const qs = u.searchParams.toString()
      window.history.replaceState({}, '', `${u.pathname}${qs ? `?${qs}` : ''}${u.hash || ''}`)
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusLeadId, items, isLoading, isFetching])

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text) } catch { }
    try { window?.toast?.show?.('복사되었습니다', 'success') } catch { }
  }

  const LANDING_CRM_MEMO = '랜딩 페이지 고객 자동등록'
  const registerSelectedToCustomerMgmt = async () => {
    const src = selected?.source
    if (!src || !['PERSONAL_LANDING', 'CATEGORY_LANDING', 'MANUAL_UPLOAD'].includes(src)) return
    const name = (selected?.customerName || '').trim()
    const phone = String(selected?.customerPhone || '').replace(/\D/g, '')
    if (!name) {
      try { window?.toast?.show?.('이름을 확인해 주세요.', 'error') } catch { }
      return
    }
    if (!(phone.length === 10 || phone.length === 11)) {
      try { window?.toast?.show?.('연락처는 숫자 10~11자리로 등록할 수 있습니다.', 'error') } catch { }
      return
    }
    setCrmRegisterBusy(true)
    try {
      await api.post('/customer-management/customers', {
        customerName: name,
        customerPhone: phone,
        memo: LANDING_CRM_MEMO,
      }, { __silent: true })
      try { window?.toast?.show?.('고객관리에 등록되었습니다.', 'success') } catch { }
      await queryClient.invalidateQueries({ queryKey: ['customer-management', 'lookup-by-phone'] })
    } catch (e) {
      const st = e?.response?.status
      const msg = e?.response?.data?.message || e?.message || '등록에 실패했습니다.'
      try {
        window?.toast?.show?.(st === 409 ? '이미 동일 연락처로 등록된 고객이 있습니다.' : msg, 'error')
      } catch { }
    } finally {
      setCrmRegisterBusy(false)
    }
  }

  const statusLabel = (s) => {
    const status = String(s || 'NEW').toUpperCase()
    if (status === 'DONE' || status === 'COMPLETED') return '처리완료'
    if (status === 'CONTACTED') return '연락함'
    if (status === 'CANCELLED') return '보류'
    return '미처리'
  }

  const setStatus = async (id, status, source) => {
    if (!id) return
    if (source === 'ORG_HOMEPAGE' && !canUseOrgHomepageLeads) return
    setStatusSaving(true)
    try {
      // 소스에 따라 다른 API 호출
      if (source === 'CATEGORY_LANDING') {
        await api.patch(`/category-landing-leads/me/${encodeURIComponent(id)}/status`, { status }, { __silent: true })
      } else if (source === 'ORG_HOMEPAGE') {
        await api.patch(`/org-landing-leads/me/${encodeURIComponent(id)}/status`, { status }, { __silent: true })
      } else if (source === 'MANUAL_UPLOAD') {
        await api.patch(`/sales-manual-leads/me/${encodeURIComponent(id)}/status`, { status }, { __silent: true })
      } else {
        await api.patch(`/leads/me/${encodeURIComponent(id)}/status`, { status }, { __silent: true })
      }
      try { window?.toast?.show?.('처리상태가 변경되었습니다', 'success') } catch { }
      if (canUseOrgHomepageLeads) await orgLeadsQuery.refetch()
      await personalLeadsQuery.refetch()
      await categoryLeadsQuery.refetch()
      await manualLeadsQuery.refetch()
      if (selected && String(selected.id) === String(id)) {
        setSelected({ ...selected, status })
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '상태 변경 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setStatusSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">DB수집관리</h1>
            <MenuGuideButton slug="consultations" title="DB수집관리 가이드" />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
            onClick={() => setStatsModalOpen(true)}
          >
            페이지 통계
          </button>
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
            disabled={isFetching}
            onClick={() => {
              if (canUseOrgHomepageLeads) orgLeadsQuery.refetch()
              personalLeadsQuery.refetch()
              categoryLeadsQuery.refetch()
              manualLeadsQuery.refetch()
              salesStatsQuery.refetch()
            }}
          >
            {isFetching ? '새로고침…' : '새로고침'}
          </button>
        </div>
      </div>

      {statsModalOpen ? (
        <div className="fixed inset-0 z-[58]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setStatsModalOpen(false)}
            aria-label="페이지 통계 닫기"
          />
          <div className="relative mx-auto max-w-5xl px-3 py-6 sm:px-4 sm:py-8 pointer-events-none">
            <div
              className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden flex max-h-[min(90vh,960px)] flex-col"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sales-stats-modal-title"
            >
              <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-3 border-b sm:px-5 sm:py-3.5">
                <div id="sales-stats-modal-title" className="font-bold text-sm sm:text-base">
                  페이지 통계
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
                  onClick={() => setStatsModalOpen(false)}
                >
                  닫기
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {canUseOrgHomepageLeads ? '개인홈·조직홈·홍보페이지' : '개인홈·홍보페이지'} 조회 수와, 상담 요청·완료 건수(집계 전용 로그, 리드 원본 삭제와 무관)를 보여줍니다.
                        페이지 조회·상담 집계는 기능 적용 이후부터 쌓이며, 최대 1년 보관 후 삭제됩니다. 아래 상담 목록은 기존과 같이 최대 30일만 유지됩니다.
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {canUseOrgHomepageLeads && (asArray(salesStatsQuery.data?.orgFilterOptions)).length > 0 ? (
                        <select
                          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm max-w-[220px]"
                          value={statsOrgId}
                          onChange={(e) => setStatsOrgId(e.target.value)}
                          title="조직홈페이지 열람·상담 집계 범위"
                        >
                          <option value="">조직홈: 전체 합산</option>
                          {(asArray(salesStatsQuery.data?.orgFilterOptions)).map((o) => (
                            <option key={o.id} value={String(o.id)}>{o.name || `조직 #${o.id}`}</option>
                          ))}
                        </select>
                      ) : null}
                      <select
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        value={statsPreset}
                        onChange={(e) => setStatsPreset(e.target.value)}
                      >
                        {STATS_PRESETS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {salesStatsQuery.isLoading ? (
                    <div className="text-sm text-slate-600">통계 불러오는 중…</div>
                  ) : salesStatsQuery.isError ? (
                    <div className="text-sm text-amber-800">통계를 불러오지 못했습니다. 잠시 후 새로고침 해 주세요.</div>
                  ) : salesStatsQuery.data ? (
                    <>
                      <div className={`grid gap-3 ${canUseOrgHomepageLeads ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                        <ChannelStatsCard title="개인홈페이지" m={salesStatsQuery.data.personalLanding} />
                        {canUseOrgHomepageLeads && <ChannelStatsCard title="조직홈페이지" m={salesStatsQuery.data.orgLanding} />}
                        <ChannelStatsCard title="홍보용페이지" m={salesStatsQuery.data.categoryLanding} />
                      </div>
                      {Array.isArray(salesStatsQuery.data.monthly) && salesStatsQuery.data.monthly.length > 0 ? (
                        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                          <button
                            type="button"
                            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50/80"
                            onClick={() => setMonthlyStatsOpen((v) => !v)}
                            aria-expanded={monthlyStatsOpen}
                          >
                            <span className="text-sm font-semibold text-slate-800">최근 12개월 월별 통계</span>
                            <span className="shrink-0 text-xs font-medium text-slate-500">{monthlyStatsOpen ? '접기' : '펼치기'}</span>
                          </button>
                          {monthlyStatsOpen ? (
                            <>
                              <div className="overflow-x-auto border-t border-slate-200">
                                <table className="w-full min-w-[720px] text-left text-xs">
                                  <thead>
                                    <tr className="border-b bg-slate-50 text-slate-600">
                                      <th className="px-3 py-2 font-semibold">년월</th>
                                      <th className="px-3 py-2 font-semibold">개인홈</th>
                                      {canUseOrgHomepageLeads && <th className="px-3 py-2 font-semibold">조직홈</th>}
                                      <th className="px-3 py-2 font-semibold">홍보용</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {salesStatsQuery.data.monthly.map((row) => (
                                      <tr key={row.yearMonth} className="align-top">
                                        <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{row.yearMonth}</td>
                                        <td className="px-3 py-2"><MonthlyMetricLine m={row.personalLanding} /></td>
                                        {canUseOrgHomepageLeads && <td className="px-3 py-2"><MonthlyMetricLine m={row.orgLanding} /></td>}
                                        <td className="px-3 py-2"><MonthlyMetricLine m={row.categoryLanding} /></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="border-t border-slate-200 px-3 py-2 text-[11px] text-slate-500">
                                {canUseOrgHomepageLeads
                                  ? '최근 12개월(당월은 오늘까지 반영). 조직홈 열은 위에서 선택한 조직 필터(전체 또는 단일 조직)를 따릅니다.'
                                  : '최근 12개월(당월은 오늘까지 반영).'}
                              </div>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* 탭 */}
      <div className="overflow-x-auto border-b border-sgez-border">
        <div className="flex min-w-max items-center gap-1">
          <button
            type="button"
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'ALL'
                ? 'border-brand-navy text-brand-navy'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => {
              setTab('ALL')
              setChannelFilter('ALL')
            }}
          >
            전체
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{allLeadsCount}</span>
          </button>
          {canUseOrgHomepageLeads ? (
          <button
            type="button"
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'ORG_HOMEPAGE'
                ? 'border-brand-navy text-brand-navy'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => {
              setTab('ORG_HOMEPAGE')
              setChannelFilter('ALL')
            }}
          >
            조직홈페이지
            <span className="ml-1 text-xs text-slate-400">(리쿠르팅)</span>
            <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] ${orgPendingCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
              {orgLeads.length}
            </span>
          </button>
          ) : null}
          <button
            type="button"
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'PERSONAL_LANDING'
                ? 'border-brand-navy text-brand-navy'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => {
              setTab('PERSONAL_LANDING')
              setChannelFilter('ALL')
            }}
          >
            개인/홍보용페이지
            <span className="ml-1 text-xs text-slate-400">(일반 고객)</span>
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{personalLeads.length + categoryLeads.length}</span>
          </button>
          <button
            type="button"
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'MANUAL'
                ? 'border-brand-navy text-brand-navy'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => {
              setTab('MANUAL')
              setChannelFilter('ALL')
            }}
          >
            직접등록
            <span className="ml-1 text-xs text-slate-400">(건별·일괄)</span>
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{manualLeads.length}</span>
          </button>
        </div>
      </div>

      {tab === 'MANUAL' && (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm">
            <div>
              <div className="font-semibold text-slate-900">직접등록</div>
              <p className="mt-1 text-xs text-slate-500">내부 DB 수집용입니다. 유입 경로는 &quot;직접등록&quot;으로 표시됩니다.</p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                onClick={() => setManualOneModalOpen(true)}
              >
                건별등록
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
                onClick={() => setManualBulkModalOpen(true)}
              >
                일괄등록
              </button>
            </div>
          </div>

          {manualOneModalOpen ? (
            <div className="fixed inset-0 z-[59]">
              <button
                type="button"
                className="absolute inset-0 bg-black/40"
                disabled={manualSubmitting}
                onClick={() => { if (!manualSubmitting) setManualOneModalOpen(false) }}
                aria-label="건별등록 닫기"
              />
              <div className="relative mx-auto max-w-lg px-3 py-8 sm:px-4 pointer-events-none">
                <div
                  className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden flex flex-col max-h-[min(90vh,560px)]"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="manual-one-modal-title"
                >
                  <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-3 border-b sm:px-5">
                    <div id="manual-one-modal-title" className="font-bold text-sm sm:text-base">건별등록</div>
                    <button
                      type="button"
                      className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                      disabled={manualSubmitting}
                      onClick={() => setManualOneModalOpen(false)}
                    >
                      닫기
                    </button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-3 text-sm">
                    <p className="text-xs text-slate-500">이름·연락처는 필수입니다. 연락처는 숫자 10~11자리로 입력해 주세요.</p>
                    <div className="grid gap-2">
                      <input className="rounded border px-2 py-2 text-sm" placeholder="이름" value={manualName} onChange={(e) => setManualName(e.target.value)} />
                      <input className="rounded border px-2 py-2 text-sm" placeholder="연락처(숫자)" value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} />
                      <input className="rounded border px-2 py-2 text-sm" placeholder="메모(선택)" value={manualMessage} onChange={(e) => setManualMessage(e.target.value)} />
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                      <button
                        type="button"
                        className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                        disabled={manualSubmitting}
                        onClick={() => setManualOneModalOpen(false)}
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                        disabled={manualSubmitting}
                        onClick={submitManualOne}
                      >
                        {manualSubmitting ? '등록 중…' : '등록'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {manualBulkModalOpen ? (
            <div className="fixed inset-0 z-[59]">
              <button
                type="button"
                className="absolute inset-0 bg-black/40"
                disabled={manualSubmitting}
                onClick={() => { if (!manualSubmitting) setManualBulkModalOpen(false) }}
                aria-label="일괄등록 닫기"
              />
              <div className="relative mx-auto max-w-2xl px-3 py-8 sm:px-4 pointer-events-none">
                <div
                  className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden flex flex-col max-h-[min(90vh,640px)]"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="manual-bulk-modal-title"
                >
                  <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-3 border-b sm:px-5">
                    <div id="manual-bulk-modal-title" className="font-bold text-sm sm:text-base">일괄등록</div>
                    <button
                      type="button"
                      className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                      disabled={manualSubmitting}
                      onClick={() => setManualBulkModalOpen(false)}
                    >
                      닫기
                    </button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-3 text-sm">
                    <p className="text-xs text-slate-500">한 줄에 하나씩: <span className="font-mono">이름,연락처[,메모]</span> (최대 200행)</p>
                    <textarea
                      className="w-full min-h-[200px] rounded border px-2 py-2 text-sm font-mono"
                      placeholder={`홍길동,01012345678,메모\n김철수,01099998888`}
                      value={manualBulk}
                      onChange={(e) => setManualBulk(e.target.value)}
                    />
                    <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                      <button
                        type="button"
                        className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                        disabled={manualSubmitting}
                        onClick={() => setManualBulkModalOpen(false)}
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                        disabled={manualSubmitting}
                        onClick={submitManualBulk}
                      >
                        {manualSubmitting ? '처리 중…' : '등록'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}

      {canUseOrgHomepageLeads && tab === 'ORG_HOMEPAGE' && (
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">미처리</div>
            <div className="mt-2 text-2xl font-black text-slate-900">{orgPendingCount}</div>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">연락함</div>
            <div className="mt-2 text-2xl font-black text-slate-900">{orgContactedCount}</div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">처리완료</div>
            <div className="mt-2 text-2xl font-black text-slate-900">{orgDoneCount}</div>
          </div>
        </div>
      )}

      {/* 개인/홍보용페이지 탭에서만 채널 필터 표시 */}
      {tab === 'PERSONAL_LANDING' && (
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <span className="text-sm text-slate-600">채널:</span>
          <button
            type="button"
            className={`rounded border px-3 py-1.5 text-sm ${channelFilter === 'ALL' ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white hover:bg-slate-50'}`}
            onClick={() => setChannelFilter('ALL')}
          >
            전체
          </button>
          <button
            type="button"
            className={`rounded border px-3 py-1.5 text-sm ${channelFilter === 'ORG_LANDING' ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white hover:bg-slate-50'}`}
            onClick={() => setChannelFilter('ORG_LANDING')}
          >
            개인홈페이지
          </button>
          <button
            type="button"
            className={`rounded border px-3 py-1.5 text-sm ${channelFilter === 'CATEGORY_LANDING' ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white hover:bg-slate-50'}`}
            onClick={() => setChannelFilter('CATEGORY_LANDING')}
          >
            홍보용페이지
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="rounded border p-4 text-sm text-slate-600">불러오는 중…</div>
      ) : isError ? (
        <div className="rounded border p-4 text-sm text-red-600">상담 내역을 불러오지 못했습니다.</div>
      ) : items.length === 0 ? (
        <div className="rounded border p-6 text-center text-sm text-slate-600">
          {tab === 'MANUAL' ? '직접 등록한 문의가 없습니다. 상단의 건별등록 또는 일괄등록으로 추가해 주세요.' : '아직 접수된 상담 문의가 없습니다.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-sgez-border-strong bg-sgez-surface">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-sgez-surface-sunken">
                <tr className="border-b border-sgez-border-strong text-left text-xs text-slate-500">
                  <th className="py-3 px-4">상태</th>
                  <th className="py-3 px-4">시각</th>
                  <th className="py-3 px-4">출처</th>
                  <th className="py-3 px-4">이름</th>
                  <th className="py-3 px-4">연락처</th>
                  <th className="py-3 px-4">희망시간</th>
                  <th className="py-3 px-4">담당</th>
                  <th className="py-3 px-4">지원 정보</th>
                  <th className="py-3 px-4">메모</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((it) => (
                  <tr
                    key={`${it.source || 'ORG'}-${it.id || `${it.customerPhone}-${it.createdAt}`}`}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => { setSelected(it); setOpen(true) }}
                  >
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold border ${
                        ['DONE', 'COMPLETED'].includes(String(it?.status || 'NEW').toUpperCase()) 
                          ? 'bg-green-50 border-green-200 text-green-700' 
                          : ['CONTACTED'].includes(String(it?.status || 'NEW').toUpperCase())
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : ['CANCELLED'].includes(String(it?.status || 'NEW').toUpperCase())
                          ? 'bg-red-50 border-red-200 text-red-700'
                          : 'bg-amber-50 border-amber-200 text-amber-700'
                      }`}>
                        {statusLabel(it?.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600 whitespace-nowrap">
                      {formatMenuInstant(it, 'createdAt')}
                    </td>
                    <td className="py-3 px-4 text-xs">
                      <div className="space-y-0.5">
                        <div className="font-medium">{it.channel || it.sourceLabel || (it.source === 'MANUAL_UPLOAD' ? '직접등록' : it.source === 'CATEGORY_LANDING' ? '홍보용페이지' : it.source === 'PERSONAL_LANDING' ? '개인홈페이지' : '조직홈페이지')}</div>
                        {it.categoryLabel && <div className="text-slate-500">{it.categoryLabel}</div>}
                        {manualLeadCreatorLabel(it) && <div className="text-slate-500">등록: {manualLeadCreatorLabel(it)}</div>}
                        {it.surveyCompleted && <div className="text-blue-600">✓ 설문완료</div>}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-medium">{it.customerName || '-'}</td>
                    <td className="py-3 px-4 whitespace-nowrap">{it.customerPhone || '-'}</td>
                    <td className="py-3 px-4 text-xs text-slate-600">{it.preferredContactTime || '-'}</td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      {['ORG_HOMEPAGE', 'PERSONAL_LANDING', 'CATEGORY_LANDING', 'MANUAL_UPLOAD'].includes(it.source)
                        ? (it.assignedUserName || (it.assignedUserId != null ? `#${it.assignedUserId}` : '—'))
                        : '—'}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      {it.source === 'ORG_HOMEPAGE' ? (
                        <div className="space-y-0.5">
                          {buildOrgLeadResponseRows(it.formResponses).slice(0, 2).map((row) => (
                            <div key={`${it.id}-${row.key}`} className="truncate">
                              <span className="font-medium text-slate-700">{row.label}</span> {row.value}
                            </div>
                          ))}
                          {buildOrgLeadResponseRows(it.formResponses).length === 0 ? '-' : null}
                        </div>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600 truncate max-w-[320px]">{it.message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 text-xs text-slate-500 border-t">
            항목을 클릭하면 상세 내용을 확인할 수 있습니다.
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60]">
          <button type="button" className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} aria-label="닫기" />
          <div className="relative mx-auto w-full max-w-[51.2rem] px-3 py-6 sm:px-4 sm:py-8">
            <div className="rounded-2xl bg-white shadow-xl border overflow-hidden flex max-h-[min(88vh,900px)] flex-col">
              <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-3 border-b sm:px-5 sm:py-3.5">
                <div className="font-bold text-sm sm:text-base">상담 문의 상세</div>
                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setOpen(false)}>닫기</button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-3">
                {(() => {
                  const sourceText = `${selected?.channel || selected?.sourceLabel || (selected?.source === 'MANUAL_UPLOAD' ? '직접등록' : selected?.source === 'CATEGORY_LANDING' ? '홍보용페이지' : selected?.source === 'PERSONAL_LANDING' ? '개인홈페이지' : '조직홈페이지')}${selected?.categoryLabel ? ` (${selected.categoryLabel})` : ''}`
                  const creatorText = manualLeadCreatorLabel(selected)
                  const surveyState = selected?.source === 'CATEGORY_LANDING'
                    ? (selected?.surveyCompleted ? '완료' : '미완료')
                    : '—'
                  const atText = formatMenuInstant(selected, 'createdAt')
                  return (
                    <div className="rounded-lg bg-slate-50 border px-3 py-2.5 text-xs sm:text-sm">
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-500">시각</div>
                          <div className="text-slate-800 font-medium leading-snug break-all">{atText}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-500">출처</div>
                          <div className="text-slate-800 font-semibold leading-snug break-words">{sourceText}</div>
                          {creatorText && <div className="mt-0.5 text-[11px] leading-tight text-slate-500 break-words">등록: {creatorText}</div>}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-500">이름</div>
                          <div className="text-slate-800 font-semibold truncate">{selected?.customerName || '-'}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-500">연락처</div>
                          <div className="text-slate-800 font-semibold truncate">{selected?.customerPhone || '-'}</div>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1 border-t border-slate-200/80 pt-2">
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-500">상태</div>
                          <div className="text-slate-800 font-semibold leading-tight">{statusLabel(selected?.status)}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-500">설문</div>
                          <div className={`font-semibold leading-tight ${selected?.surveyCompleted ? 'text-blue-600' : 'text-slate-600'}`}>
                            {surveyState}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-500">희망 연락</div>
                          <div className="text-slate-700 leading-tight break-words line-clamp-2">{selected?.preferredContactTime || '—'}</div>
                        </div>
                      </div>
                    </div>
                  )
                })()}

                {((SHOW_ORG_LANDING_LEAD_ASSIGN_UI && selected?.source === 'ORG_HOMEPAGE')
                  || ['PERSONAL_LANDING', 'CATEGORY_LANDING', 'MANUAL_UPLOAD'].includes(selected?.source))
                  && selected?.canAssignLead && (selected?.assignableRootOrgId || selected?.orgId) ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5 text-xs">
                    <div className="font-semibold text-amber-900 mb-1">담당자 배정</div>
                    {(selected?.assignableRootOrgName || selected?.orgName) ? (
                      <p className="mb-2 text-[11px] text-amber-900/80">
                        <span className="font-medium">{selected.assignableRootOrgName || selected.orgName}</span> 및 그 하위 조직 소속에게 배정할 수 있습니다.
                      </p>
                    ) : (
                      <p className="mb-2 text-[11px] text-amber-900/80">
                        리드가 속한 조직과 그 하위 조직 소속에게 배정할 수 있습니다.
                      </p>
                    )}
                    {assignLoading ? (
                      <p className="text-slate-600">조직원 목록을 불러오는 중…</p>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="search"
                          autoComplete="off"
                          placeholder="이름 또는 이메일로 검색"
                          className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
                          value={assignSearch}
                          onChange={(e) => setAssignSearch(e.target.value)}
                        />
                        <div className="max-h-40 overflow-y-auto rounded border border-slate-300 bg-white divide-y divide-slate-100">
                          <button
                            type="button"
                            className={`flex w-full items-center px-2 py-2 text-left text-sm hover:bg-slate-50 ${
                              assignPick === '' ? 'bg-amber-100/80 font-medium text-amber-950' : 'text-slate-800'
                            }`}
                            onClick={() => setAssignPick('')}
                          >
                            미배정 (관리자만 목록)
                          </button>
                          {assignFilteredSorted.map((u) => {
                            const idStr = String(u.id)
                            const picked = assignPick === idStr
                            const primary = `${assigneeDisplayName(u)}${assigneeOrgSuffix(u)}`
                            const sub = u.email && String(u.name || '').trim() ? u.email : ''
                            return (
                              <button
                                key={u.id}
                                type="button"
                                className={`flex w-full flex-col items-start gap-0.5 px-2 py-2 text-left text-sm hover:bg-slate-50 ${
                                  picked ? 'bg-amber-100/80 font-medium text-amber-950' : 'text-slate-800'
                                }`}
                                onClick={() => setAssignPick(idStr)}
                              >
                                <span>{primary}</span>
                                {sub ? <span className="text-[11px] font-normal text-slate-500">{sub}</span> : null}
                              </button>
                            )
                          })}
                        </div>
                        {assignFilteredSorted.length === 0 && assignSearch.trim() ? (
                          <p className="text-[11px] text-slate-600">검색 결과가 없습니다.</p>
                        ) : null}
                        <button
                          type="button"
                          className="rounded bg-amber-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-950 disabled:opacity-50"
                          disabled={assignSaving}
                          onClick={submitLeadAssign}
                        >
                          {assignSaving ? '저장 중…' : '배정 저장'}
                        </button>
                      </div>
                    )}
                    <p className="mt-2 text-[11px] text-amber-900/85">
                      {selected?.source === 'ORG_HOMEPAGE'
                        ? '배정 후에는 관리자와 담당자 모두 목록에서 볼 수 있습니다. 미배정 리드는 관리자만 볼 수 있습니다.'
                        : '배정 후 담당자와 같은 조직·상·하위 조직 소속도 목록에서 확인할 수 있습니다. 조직 관리자는 관리 범위 내 리드를 모두 볼 수 있습니다.'}
                    </p>
                  </div>
                ) : null}

                <div>
                  <div className="text-sm font-semibold">문의 내용</div>
                  <div className="mt-1.5 whitespace-pre-line text-sm text-slate-700 rounded-lg border p-2.5 max-h-40 overflow-y-auto">
                    {selected?.message || '-'}
                  </div>
                </div>

                {selected?.source === 'ORG_HOMEPAGE' && buildOrgLeadResponseRows(selected?.formResponses).length > 0 && (
                  <div>
                    <div className="text-sm font-semibold">지원서 정보</div>
                    <ul className="mt-1.5 space-y-1.5">
                      {buildOrgLeadResponseRows(selected?.formResponses).map((row) => (
                        <li key={`org-response-${row.key}`} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
                          <div className="text-[11px] font-semibold text-slate-600 leading-tight">{row.label}</div>
                          <div className="mt-0.5 whitespace-pre-wrap text-xs sm:text-sm text-slate-800 leading-snug">
                            {row.value || '—'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selected?.surveyCompleted && Array.isArray(selected?.surveyAnswerRows) && selected.surveyAnswerRows.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold">설문 응답</div>
                    <ul className="mt-1.5 space-y-1.5">
                      {selected.surveyAnswerRows.map((row, idx) => (
                        <li key={String(row?.id ?? `survey-row-${idx}`)} className="rounded-md border border-blue-100 bg-blue-50/80 px-2.5 py-2">
                          <div className="text-[11px] font-semibold text-slate-600 leading-tight">{row?.label || row?.id || '문항'}</div>
                          <div className="mt-0.5 whitespace-pre-wrap text-xs sm:text-sm text-slate-800 leading-snug">
                            {row?.answer != null && String(row.answer).trim() !== '' ? String(row.answer) : '—'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selected?.surveyCompleted
                  && (!Array.isArray(selected?.surveyAnswerRows) || selected.surveyAnswerRows.length === 0)
                  && formatSurveyResponsesForDisplay(selected?.surveyResponses) && (
                  <div>
                    <div className="text-sm font-semibold">설문 응답</div>
                    <div className="mt-1.5 max-h-48 overflow-y-auto whitespace-pre-wrap font-mono text-xs text-slate-700 rounded-lg border p-2.5 bg-blue-50">
                      {formatSurveyResponsesForDisplay(selected.surveyResponses)}
                    </div>
                  </div>
                )}

                <div className="space-y-2 border-t border-slate-200 pt-3">
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <div className="shrink-0 self-start sm:self-auto">
                      {['ORG_HOMEPAGE', 'MANUAL_UPLOAD', 'PERSONAL_LANDING', 'CATEGORY_LANDING'].includes(selected?.source) ? (
                        <div className="flex items-center gap-2">
                          <label htmlFor="lead-detail-status" className="whitespace-nowrap text-xs font-semibold text-slate-600">
                            처리 상태
                          </label>
                          <select
                            id="lead-detail-status"
                            className="min-w-[9rem] rounded border border-slate-300 bg-white py-2 pl-3 pr-9 text-sm font-medium text-slate-800 box-border"
                            style={{ textIndent: 0 }}
                            disabled={statusSaving || !selected?.id}
                            value={(() => {
                              const s = String(selected?.status || 'NEW').toUpperCase()
                              if (s === 'COMPLETED') return 'DONE'
                              if (['NEW', 'CONTACTED', 'DONE', 'CANCELLED'].includes(s)) return s
                              return 'NEW'
                            })()}
                            onChange={(e) => setStatus(selected?.id, e.target.value, selected?.source)}
                          >
                            <option value="NEW">미처리</option>
                            <option value="CONTACTED">연락함</option>
                            <option value="DONE">처리완료</option>
                            <option value="CANCELLED">보류</option>
                          </select>
                        </div>
                      ) : null}
                    </div>
                    <div className="-mx-1 flex min-w-0 w-full flex-nowrap items-center justify-end gap-x-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:thin] sm:w-auto sm:flex-1">
                      <span className="shrink-0 text-xs font-semibold whitespace-nowrap text-slate-500">연락</span>
                      <button
                        type="button"
                        className="shrink-0 rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                        disabled={!selected?.customerPhone}
                        onClick={() => copy(selected?.customerPhone || '')}
                      >
                        연락처 복사
                      </button>
                      <a
                        className={`shrink-0 rounded border px-3 py-2 text-sm font-semibold ${selected?.customerPhone ? 'hover:bg-slate-50' : 'cursor-not-allowed opacity-40'}`}
                        href={selected?.customerPhone ? `tel:${String(selected.customerPhone).replace(/[^0-9+]/g, '')}` : undefined}
                        onClick={(e) => { if (!selected?.customerPhone) e.preventDefault() }}
                      >
                        전화
                      </a>
                      <a
                        className={`shrink-0 rounded border px-3 py-2 text-sm font-semibold ${selected?.customerPhone ? 'hover:bg-slate-50' : 'cursor-not-allowed opacity-40'}`}
                        href={selected?.customerPhone ? `sms:${String(selected.customerPhone).replace(/[^0-9+]/g, '')}` : undefined}
                        onClick={(e) => { if (!selected?.customerPhone) e.preventDefault() }}
                      >
                        문자
                      </a>
                      {['PERSONAL_LANDING', 'CATEGORY_LANDING', 'MANUAL_UPLOAD'].includes(selected?.source) && (
                        <button
                          type="button"
                          className="shrink-0 rounded border border-emerald-700 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                          disabled={(() => {
                            const d = String(selected?.customerPhone || '').replace(/\D/g, '')
                            return crmRegisterBusy
                              || crmRegisteredLookup.isLoading
                              || crmAlreadyRegistered
                              || !String(selected?.customerName || '').trim()
                              || !(d.length === 10 || d.length === 11)
                          })()}
                          onClick={registerSelectedToCustomerMgmt}
                        >
                          {crmRegisterBusy ? '등록 중…' : '고객등록'}
                        </button>
                      )}
                    </div>
                  </div>
                  {crmAlreadyRegistered && ['PERSONAL_LANDING', 'CATEGORY_LANDING', 'MANUAL_UPLOAD'].includes(selected?.source) ? (
                    <p className="text-[11px] text-slate-500 leading-snug">
                      이미 고객관리에 동일 연락처로 등록된 고객입니다.
                    </p>
                  ) : null}
                </div>

                <div className="text-[11px] text-slate-500">
                  개인정보는 접수일로부터 최대 30일 보관 후 파기됩니다.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
